import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import styles from "./MenuProgressIndicator.module.css";

const RADIUS = 8;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

type MenuProgressIndicatorProps = {
  progress: number;
  expired?: boolean;
  completedAsBrandDot?: boolean;
  selected?: boolean;
  onDark?: boolean;
};

function MenuProgressIndicator({
  progress,
  expired = false,
  completedAsBrandDot = false,
  selected = false,
  onDark = false,
}: MenuProgressIndicatorProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const progressRef = useRef<SVGCircleElement>(null);
  const clamped = Math.min(100, Math.max(0, Math.round(progress)));

  useEffect(() => {
    const circle = progressRef.current;
    if (!circle) {
      return;
    }
    const targetOffset = CIRCUMFERENCE * (1 - clamped / 100);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        circle.style.strokeDashoffset = String(targetOffset);
      });
    });
  }, [clamped]);

  if (clamped >= 100) {
    if (completedAsBrandDot) {
      return (
        <span className={styles.okDot} aria-label={t("menu.progressCompleted")}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 12.5l4.2 4.2L19 7" />
          </svg>
        </span>
      );
    }
    return (
      <svg
        className={styles.check}
        viewBox="0 0 20 20"
        role="img"
        aria-label={t("menu.progressCompleted")}
      >
        <path d="M4.5 10.5l3.7 3.7 7.3-8" />
      </svg>
    );
  }

  return (
    <svg
      className={styles.ring}
      viewBox="0 0 20 20"
      role="img"
      aria-label={t("menu.progressAria", { progress: clamped })}
    >
      <circle
        className={`${styles.track} ${onDark ? styles.trackOnDark : ""}`}
        cx="10"
        cy="10"
        r={RADIUS}
      />
      <circle
        ref={progressRef}
        className={`${styles.progress} ${expired ? styles.progressExpired : ""} ${selected ? styles.progressSelected : ""} ${onDark ? styles.progressOnDark : ""}`}
        cx="10"
        cy="10"
        r={RADIUS}
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={CIRCUMFERENCE}
      />
    </svg>
  );
}

export default MenuProgressIndicator;
